// ─── Initial State ──────────────────────────────────────────────────
export const initialGameState = {
    gameId: null,
    phase: 'idle',
    players: [],
    localPlayerIndex: 0,
    currentTurnIndex: 0,
    hand: [],
    trickCards: [],
    leadSuit: null,
    trickWinnerId: null,
    showingCollection: false,
    hoveredCardIndex: -1,
    selectedCardIndex: -1,
    turnTimeRemainingMs: 30_000,
    turnTotalMs: 30_000,
    roundNumber: 1,
    roundResults: null,
    winnerId: null,
    finalScores: null,
    roundHistory: [],
    error: null,
    playerVoids: {},
};
// ─── Actions (pure state transitions) ──────────────────────────────
export function setGameStarted(_state, gameId, players, localPlayerIndex) {
    return {
        ...initialGameState,
        gameId,
        players,
        localPlayerIndex,
        // Round 1 is an open round — no calling phase, straight to playing
        phase: 'playing',
        roundNumber: 1,
    };
}
export function setHand(state, hand) {
    return { ...state, hand };
}
export function setCallPhase(state, currentTurnIndex) {
    return {
        ...state,
        phase: 'calling',
        currentTurnIndex,
        turnTimeRemainingMs: 30_000,
        turnTotalMs: 30_000,
    };
}
export function setPlayPhase(state, currentTurnIndex) {
    // Do NOT clear trickCards here — game:playPhase fires every turn (not just at the
    // calling→playing transition), so clearing mid-trick would wipe the table.
    // trickCards is cleared by clearTrick() after game:trickWon, and by setNewRound().
    return {
        ...state,
        phase: 'playing',
        currentTurnIndex,
        turnTimeRemainingMs: 30_000,
        turnTotalMs: 30_000,
    };
}
export function applyCallMade(state, playerId, call) {
    const player = state.players.find((p) => p.id === playerId);
    if (player?.call === call)
        return state;
    const players = state.players.map((p) => (p.id === playerId ? { ...p, call } : p));
    return { ...state, players };
}
export function applyCardPlayed(state, playerId, card) {
    const alreadyInTrick = state.trickCards.some((tc) => tc.playerId === playerId && tc.card.suit === card.suit && tc.card.rank === card.rank);
    if (alreadyInTrick)
        return state;
    const playerCount = state.players.length || 4;
    // When the previous trick is still displayed (all playerCount cards shown) and a new
    // card arrives, it means a new trick is starting. Reset the display immediately so the
    // old trick animation doesn't mix with the new trick's cards.
    const isNewTrick = state.trickCards.length >= playerCount;
    const trickCards = isNewTrick ? [{ playerId, card }] : [...state.trickCards, { playerId, card }];
    const leadSuit = trickCards.length === 1 ? card.suit : state.leadSuit;
    // Remove card from hand if it's the local player
    const localPlayer = state.players[state.localPlayerIndex];
    let hand = state.hand;
    if (localPlayer && playerId === localPlayer.id) {
        hand = hand.filter((c) => !(c.suit === card.suit && c.rank === card.rank));
    }
    return {
        ...state,
        trickCards,
        leadSuit,
        hand,
        selectedCardIndex: -1,
        hoveredCardIndex: -1,
        // Clear trick winner immediately when the new trick's first card arrives so the
        // collection animation stops and the useEffect timer is cancelled cleanly.
        trickWinnerId: isNewTrick ? null : state.trickWinnerId,
        showingCollection: isNewTrick ? false : state.showingCollection,
    };
}
export function undoCardPlayed(state, playerId, card) {
    // Remove card from trickCards
    const trickCards = state.trickCards.filter((tc) => !(tc.playerId === playerId && tc.card.suit === card.suit && tc.card.rank === card.rank));
    // Re-add card to local player's hand if it was removed
    const localPlayer = state.players[state.localPlayerIndex];
    let hand = state.hand;
    if (localPlayer && playerId === localPlayer.id) {
        const alreadyInHand = hand.some((c) => c.suit === card.suit && c.rank === card.rank);
        if (!alreadyInHand)
            hand = [...hand, card];
    }
    // Restore leadSuit from whatever is now the first trick card (or null if trick is now empty)
    const leadSuit = trickCards.length > 0 ? trickCards[0].card.suit : null;
    return { ...state, trickCards, hand, leadSuit };
}
export function applyTrickWon(state, winnerId) {
    const players = state.players.map((p) => p.id === winnerId ? { ...p, tricksWon: p.tricksWon + 1 } : p);
    return { ...state, players, trickWinnerId: winnerId, showingCollection: true };
}
export function clearTrick(state) {
    return {
        ...state,
        trickCards: [],
        leadSuit: null,
        trickWinnerId: null,
        showingCollection: false,
    };
}
export function setRoundEnd(state, results) {
    const players = state.players.map((p) => {
        const r = results.find((res) => res.playerId === p.id);
        return r ? { ...p, totalScore: r.totalScore, tricksWon: 0, call: null } : p;
    });
    return {
        ...state,
        phase: 'roundEnd',
        roundResults: results,
        players,
        roundHistory: [...state.roundHistory, results],
    };
}
export function setNewRound(state, roundNumber, currentTurnIndex) {
    return {
        ...state,
        phase: 'calling',
        roundNumber,
        currentTurnIndex,
        roundResults: null,
        trickCards: [],
        leadSuit: null,
        trickWinnerId: null,
        showingCollection: false,
        hoveredCardIndex: -1,
        selectedCardIndex: -1,
        playerVoids: {},
    };
}
export function setPlayerVoids(state, voids) {
    return { ...state, playerVoids: voids };
}
export function setGameOver(state, winnerId, finalScores) {
    return { ...state, phase: 'gameOver', winnerId, finalScores };
}
export function setGameAbandoned(state) {
    return { ...state, phase: 'abandoned' };
}
export function setTurnTimer(state, remainingMs) {
    return { ...state, turnTimeRemainingMs: remainingMs };
}
export function setCurrentTurn(state, currentTurnIndex) {
    return { ...state, currentTurnIndex };
}
export function setHoveredCard(state, index) {
    return { ...state, hoveredCardIndex: index };
}
export function setSelectedCard(state, index) {
    return { ...state, selectedCardIndex: index };
}
export function setError(state, error) {
    return { ...state, error };
}
export function setPlayerDisconnected(state, playerId) {
    const players = state.players.map((p) => (p.id === playerId ? { ...p, isConnected: false } : p));
    return { ...state, players };
}
export function setPlayerReconnected(state, playerId) {
    const players = state.players.map((p) => p.id === playerId ? { ...p, isConnected: true, isAI: false } : p);
    return { ...state, players };
}
export function setPlayerReplacedByAI(state, playerId) {
    const players = state.players.map((p) => p.id === playerId ? { ...p, isAI: true, isConnected: false } : p);
    return { ...state, players };
}
export function resetGame() {
    return { ...initialGameState };
}
/**
 * Restore full client game state from a server-side reconnect snapshot.
 * Called automatically whenever the socket reconnects mid-game.
 */
export function setReconnectedState(state, payload) {
    const phaseMap = {
        calling: 'calling',
        playing: 'playing',
        roundEnd: 'roundEnd',
        gameOver: 'gameOver',
    };
    const currentTurnIndex = payload.currentPlayerId
        ? state.players.findIndex((p) => p.id === payload.currentPlayerId)
        : state.currentTurnIndex;
    const players = state.players.map((p) => {
        const scoreInfo = payload.scores.find((s) => s.playerId === p.id);
        const callValue = payload.calls[p.id];
        return {
            ...p,
            call: callValue !== undefined ? callValue : p.call,
            totalScore: scoreInfo?.score ?? p.totalScore,
            tricksWon: scoreInfo?.tricksWon ?? p.tricksWon,
            isConnected: true,
        };
    });
    return {
        ...state,
        hand: payload.hand,
        phase: phaseMap[payload.phase] ?? state.phase,
        roundNumber: payload.roundNumber,
        currentTurnIndex: currentTurnIndex >= 0 ? currentTurnIndex : state.currentTurnIndex,
        trickCards: payload.trickCards,
        leadSuit: (payload.trickCards[0]?.card.suit ?? null),
        players,
        trickWinnerId: null,
        showingCollection: false,
    };
}
//# sourceMappingURL=gameStore.js.map